import { useForm } from 'react-hook-form'

import { ApolloError } from '@apollo/client'
import { yupResolver } from '@hookform/resolvers/yup'

import { User, UserDataDocument, useSignInMutation, useUserDataQuery } from '~/app/generated/graphql'
import { gt } from '~/lib/gt'

import AppleLoginButton from '../AppleLogin/AppleLoginButtonView'
import { Button } from '../Button/Button'
import { FormInput } from '../FormInput/FormInput'
import GoogleLoginButton from '../GoogleLoginButton'
import { useToast } from '../Toast'

import { formatErrorMessages } from './formatErrorMessages'
import styles from './LoginModal.module.css'
import { LOGIN_FORM_FIELDS, loginSchema } from './LoginModalUtils'

export type SignInCallback = (user: Pick<User, 'id'> | null) => void

type LoginFormProps = {
  onLogin?: SignInCallback
  onRegisterLinkClick?: () => void
  handleClose: () => void
  onForgotPasswordClick?: () => void
}

export const LoginModal = ({ onLogin, onRegisterLinkClick, handleClose, onForgotPasswordClick }: LoginFormProps) => {
  return (
    <div className={styles.loginModal}>
      <div className={styles.formWrapper}>
        <LoginForm
          handleClose={handleClose}
          onLogin={onLogin}
          onRegisterLinkClick={onRegisterLinkClick}
          onForgotPasswordClick={onForgotPasswordClick}
        />
      </div>
      <div className={styles.imageWrapper}>
        <img src='/lp/assets/login/loginFormPhoto.jpg' alt='Login Form' className={styles.image} />
      </div>
    </div>
  )
}

export const LoginForm = ({ onLogin, onRegisterLinkClick, handleClose, onForgotPasswordClick }: LoginFormProps) => {
  const { refetch } = useUserDataQuery()
  const [signIn] = useSignInMutation({
    refetchQueries: [{ query: UserDataDocument }, 'UserData'],
    onCompleted: data => onLogin?.(data.signIn?.user ?? null),
  })
  const toast = useToast()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: yupResolver(loginSchema),
  })

  const handleErrors = (error: ApolloError) => {
    const errors = formatErrorMessages(error)
    errors?.forEach(errorMsg =>
      toast?.add({
        message: errorMsg,
        type: 'error',
        containerSelector: '#LoginToastContainer',
        size: 'small',
      })
    )
  }

  const handleSocialLogin: SignInCallback = data => {
    if (!data) return
    onLogin?.(data)
    refetch()
    handleClose()
  }

  const onSubmit = async (values: { email: string; password: string }) => {
    await signIn({
      variables: values,
      onCompleted: handleClose,
      onError: handleErrors,
    })
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
      <h2 className={styles.title}>{gt.tp('LoginForm', 'Log in')}</h2>
      <h5 className={styles.noAccount}>
        {gt.tp('LoginForm', 'Not registered yet?')}{' '}
        <span className={styles.link} onClick={onRegisterLinkClick}>
          {gt.tp('LoginForm', 'Create an account')}
        </span>
      </h5>
      <div id='LoginToastContainer' className={styles.toastContainer} />
      {LOGIN_FORM_FIELDS.map(field => (
        <div key={field.name}>
          <FormInput
            {...register(field.name)}
            type={field.type}
            label={gt.tp('LoginForm', field.title)}
            className={styles.input}
            errorMessage={errors[field.name]?.message}
          />
        </div>
      ))}
      <div className={styles.formLinkContainer} onClick={onForgotPasswordClick}>
        {gt.tp('LoginForm', 'Forgot password?')}
      </div>
      <Button className={styles.loginButton} type='submit' disabled={!isValid || isSubmitting} withArrow={false}>
        {gt.tp('LoginForm', 'Log in')}
      </Button>
      <div className={styles.otherLogin}>{gt.tp('LoginForm', 'or')}</div>
      <div className={styles.otherLoginButtons}>
        <GoogleLoginButton onLogin={handleSocialLogin} />
        <AppleLoginButton onLogin={handleSocialLogin} />
      </div>
    </form>
  )
}
